<template>
    <div class="relative min-h-screen antialiased bg-white font-sans">
        <!-- Top right actions -->
        <div class="absolute right-5 md:right-20 top-4 flex gap-4">
            <button
                v-if="!isRetailer"
                v-print="printObj"
                title="Print"
                class="p-2 rounded-full hover:bg-gray-200"
            >
                <PrintIcon class="w-5" />
            </button>

            <button
                title="Reload"
                class="p-2 rounded-full hover:bg-gray-200"
                @click="reload"
            >
                <ReplaceIcon />
            </button>
        </div>

        <!-- Invoice Body -->
        <div
            id="print-invoice"
            class="px-6 py-8 md:px-32"
        >
            <!-- Header -->
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-3xl font-bold uppercase tracking-wide text-gray-800">Invoice</h1>
                    <p class="text-sm text-gray-500">#{{ bill.bill_number }}</p>
                </div>

                <div class="flex flex-col items-end space-y-1">
                    <div class="w-28 h-28 bg-gray-100 flex items-center justify-center">Logo Here</div>
                    <div class="mt-2">
                        <qrcode-vue
                            :value="invoiceUrl"
                            :size="80"
                        />
                    </div>
                </div>
            </div>

            <!-- Meta Info -->
            <div class="grid grid-cols-2 gap-8 mt-8 text-sm text-gray-700">
                <div>
                    <p><strong>Date:</strong> {{ bill.created_at | readableDate }}</p>
                    <p><strong>Payment Method:</strong> {{ bill.payment_method }}</p>
                </div>
                <div>
                    <p><strong>Invoice URL:</strong></p>
                    <p class="text-xs break-all text-blue-500">{{ invoiceUrl }}</p>
                </div>
            </div>

            <!-- Addresses -->
            <div class="grid grid-cols-2 gap-8 mt-8 text-sm print:flex-row print:gap-8">
                <div>
                    <h3 class="font-semibold mb-1">Bill To:</h3>
                    <div class="p-2 bg-gray-50 rounded">
                        <p>{{ bill.user.name }}</p>
                        <p>{{ bill.user.kyc.business_address }}</p>
                    </div>
                </div>
                <div>
                    <h3 class="font-semibold mb-1">From:</h3>
                    <div class="p-2 bg-gray-50 rounded">
                        <p>{{ bill.wholesaler.name }}</p>
                        <p>{{ bill.wholesaler.kyc.business_address }}</p>
                    </div>
                </div>
            </div>

            <!-- Product Table -->
            <div class="mt-10">
                <h3 class="text-sm font-medium mb-2 text-gray-700">Descriptions</h3>
                <table class="w-full border border-gray-200 text-sm text-left">
                    <thead class="bg-green-100 text-gray-700">
                        <tr>
                            <th class="p-2 border">S.N.</th>
                            <th class="p-2 border">Item</th>
                            <th class="p-2 border">Qty</th>
                            <th class="p-2 border">Unit Price</th>
                            <th class="p-2 border">VAT</th>
                            <th class="p-2 border">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(transaction, index) in bill.transactions"
                            :key="transaction.id"
                            class="bg-white"
                        >
                            <td class="p-2 border text-center">{{ index + 1 }}</td>
                            <td class="p-2 border">{{ transaction.product.name }}</td>
                            <td class="p-2 border text-center">{{ transaction.qty }}</td>
                            <td class="p-2 border text-right">
                                {{ config(common.currency) }} {{ transaction.product.price }}
                            </td>
                            <td class="p-2 border text-right">{{ config(common.currency) }} {{ transaction.vat }}</td>
                            <td class="p-2 border text-right">
                                {{ config(common.currency) }} {{ transaction.grand_total }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Summary -->
            <div class="mt-6 flex justify-end">
                <div class="text-sm text-right space-y-1">
                    <p>
                        <strong>Grand Total:</strong> {{ config(common.currency) }}
                        {{ parseFloat(bill.total_amount).toFixed(2) }}
                    </p>
                    <p>
                        <strong>Paid:</strong> {{ config(common.currency) }}
                        {{ parseFloat(bill.amount_paid).toFixed(2) }}
                    </p>
                    <p v-if="parseFloat(bill.amount_remaining)">
                        <strong>Remaining:</strong> {{ config(common.currency) }}
                        {{ parseFloat(bill.amount_remaining).toFixed(2) }}
                    </p>
                </div>
            </div>

            <!-- Notes -->
            <div
                v-if="bill.notes"
                class="mt-6"
            >
                <h3 class="font-medium text-sm text-gray-700 mb-1">Notes</h3>
                <p class="p-2 bg-gray-50 rounded text-sm text-gray-600">
                    {{ bill.notes }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import print from 'vue-print-nb';
    import QrcodeVue from 'qrcode.vue';
    import common from '../common';
    import ReplaceIcon from '../Components/Icons/ReplaceIcon.vue';
    import PrintIcon from '../Components/Icons/PrintIcon.vue';

    export default {
        name: 'InvoiceComponent',

        directives: {
            print,
        },

        components: {
            QrcodeVue,
            ReplaceIcon,
            PrintIcon,
        },

        props: {
            bill: {
                type: Object,
                required: true,
            },
            invoiceBaseUrl: {
                type: String,
                default: `${process.env.MIX_SENTRY_DSN_PUBLIC}/invoice/`, // adjust per your app
            },
        },

        data() {
            return {
                common,
                printObj: {
                    id: '#print-invoice',
                    popTitle: 'Gumtt-Invoice',
                    extraHead: `
                    <link href="https://unpkg.com/tailwindcss@^2/dist/tailwind.min.css" rel="stylesheet">
                    <style>
                        @media print {
                        body {
                            zoom: 85%;
                        }
                        }
                    </style>
                    `,
                },
            };
        },

        computed: {
            invoiceUrl() {
                return `${this.invoiceBaseUrl}${this.bill.id}`;
            },
        },

        methods: {
            reload() {
                window.location.reload();
            },
        },
    };
</script>
